import {
  Bullet,
  Circle,
  Root,
  color,
  ease,
  percent,
} from "@amcharts/amcharts5";
import {
  AxisRendererCircular,
  AxisRendererRadial,
  RadarChart,
  RadarLineSeries,
} from "@amcharts/amcharts5/radar";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { CategoryAxis, ValueAxis } from "@amcharts/amcharts5/xy";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useLayoutEffect, useState } from "react";
import { STATS_CHART } from "../../../lib/constants.js";

export default function StatsChart({ onSelectStat, selectedStat, stats }) {
  const [series, setSeries] = useState(null);
  const { breakpoints, palette, typography } = useTheme();
  const isMediumScreen = useMediaQuery(breakpoints.down("md"));

  useLayoutEffect(() => {
    /**
     * General settings
     */
    const root = Root.new("stats-chart", {
      useSafeResolution: false,
    });

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      RadarChart.new(root, {
        layout: root.horizontalLayout,
        radius: percent(100),
      })
    );

    /**
     * Y Axis
     */
    const yAxis = chart.yAxes.push(
      ValueAxis.new(root, {
        min: STATS_CHART.MIN,
        max: STATS_CHART.MAX,
        strictMinMax: true,
        renderer: AxisRendererRadial.new(root, {
          minGridDistance: 10,
          radius: percent(100),
        }),
      })
    );

    yAxis.get("renderer").labels.template.setAll({
      visible: false,
    });

    yAxis.get("renderer").grid.template.setAll({
      strokeOpacity: 0.2,
      stroke: color(palette.primary.main),
    });

    /**
     * X Axis
     */
    const xAxis = chart.xAxes.push(
      CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: "category",
        renderer: AxisRendererCircular.new(root, {
          minGridDistance: 1,
        }),
      })
    );

    xAxis.get("renderer").labels.template.setAll({
      fontFamily: typography.fontFamily,
      fontSize: 14,
      fill: color(palette.text.primary),
      radius: 10,
      oversizedBehavior: "truncate",
      maxWidth: isMediumScreen ? 45 : 120,
      ellipsis: ".",
    });

    xAxis.get("renderer").grid.template.setAll({
      forceHidden: false,
      strokeOpacity: 0.5,
      stroke: color(palette.primary.main),
    });

    xAxis.data.setAll(stats);

    /**
     * Series
     */
    const series = chart.series.push(
      RadarLineSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        categoryXField: "category",
        connectEnds: true,
        fill: color(palette.secondary.main),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: 1,
      stroke: color(palette.secondary.main),
    });

    series.bullets.push(function (root) {
      const circle = Circle.new(root, {
        radius: 8,
        fill: series.get("fill"),
        tooltipText: "{categoryX}: {valueY}",
        interactive: true,
        stateAnimationDuration: 500,
        stateAnimationEasing: ease.out(ease.cubic),
      });

      circle.events.on("click", ({ target }) => {
        const category = target.dataItem.get("categoryX");

        onSelectStat(category);
      });

      return Bullet.new(root, {
        sprite: circle,
      });
    });

    setSeries(series);

    return () => {
      root.dispose();
    };
  }, [isMediumScreen, onSelectStat, palette, stats, typography]);

  useEffect(() => {
    if (series && selectedStat) {
      const updatedStats = stats.map((stat) =>
        stat.category === selectedStat.category
          ? { ...stat, value: selectedStat.value }
          : stat
      );

      series.data.setAll(updatedStats);
    }
  }, [selectedStat, series, stats]);

  return (
    <Box position="relative" width="100%">
      <Box
        bottom={0}
        height={30}
        position="absolute"
        width="100%"
        zIndex="999"
      />
      <Box id="stats-chart" sx={{ width: "100%", height: "700px" }}></Box>
    </Box>
  );
}
